import { useState } from "react";
import { X, Search, User } from "lucide-react";
import type { DoctorListItem } from "../../services/api/admin-order.types";

interface DoctorSelectModalProps {
  onClose: () => void;
  onSelect: (doctor: DoctorListItem) => void;
  doctors: DoctorListItem[];
  loading?: boolean;
}

const DoctorSelectModal: React.FC<DoctorSelectModalProps> = ({ onClose, onSelect, doctors, loading }) => {
  const [search, setSearch] = useState("");

  const filtered = doctors.filter(
    (d) =>
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.mobileNumber.includes(search)
  );

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1000] flex items-center justify-center animate-fadeIn" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-[520px] w-[95%] max-h-[80vh] flex flex-col shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-sm font-bold text-slate-800">Select Doctor</h3>
          <button className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search doctor by name or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-2">
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading doctors...</div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No doctors found</div>
          ) : (
            filtered.map((doctor) => (
              <div
                key={doctor.id}
                className="flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 cursor-pointer transition-all group"
                onClick={() => onSelect(doctor)}
              >
                <div className="flex items-center gap-3">
                  {doctor.profileImage ? (
                    <img src={doctor.profileImage} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-500">
                      <User size={18} />
                    </div>
                  )}
                  <div>
                    <div className="text-xs font-bold text-slate-800">{doctor.name}</div>
                    <div className="text-[0.65rem] text-slate-400">{doctor.mobileNumber}</div>
                    {doctor.registrationNumber && (
                      <div className="text-[0.6rem] text-slate-400 mt-0.5">Reg: {doctor.registrationNumber}</div>
                    )}
                    {doctor.doctorEducation && (
                      <div className="text-[0.6rem] text-slate-400">{doctor.doctorEducation} · {doctor.doctorSpecialization}</div>
                    )}
                  </div>
                </div>
                <button className="px-3 py-1.5 rounded-lg text-[0.7rem] font-semibold bg-indigo-500 text-white opacity-0 group-hover:opacity-100 transition-opacity border-none cursor-pointer">
                  Select
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default DoctorSelectModal;
